import { formatPropertyAddress } from '../utils/propertyAddress';
import { propertyFetch } from '../services/idxPropertyClient';
import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import { SITE_CONFIG } from '../config';
import { SEO_CONFIG } from '../config/seo';
import { useTheme } from '../contexts/ThemeContext';
import { useTranslation } from 'react-i18next';
import './IDXTownPage.css';
import SEOLinks from './SEOLinks';

const FountainValleyRealEstatePage = () => {
  const navigate = useNavigate();
  const { isDarkMode } = useTheme();
  const { t } = useTranslation();
  const [properties, setProperties] = useState([]);
  const [propertyImages, setPropertyImages] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    document.body.classList.remove('intro-active');
    document.body.style.overflow = 'auto';
    document.body.style.position = 'static';
    if (isDarkMode) {
      document.body.classList.add('dark-mode');
    } else {
      document.body.classList.remove('dark-mode');
    }
    return () => {
      document.body.style.overflow = '';
      document.body.style.position = '';
    };
  }, [isDarkMode]);

  useEffect(() => {
    const fetchProperties = async () => {
      try {
        const response = await propertyFetch(`https://tophamtechbe.com/api/treas/idx/v1/retrieve-property-summary?city=Fountain+Valley&county=Orange&state=CA&firstPhoto=true&hasMedia=true&limit=9`, {});
        if (response.ok) {
          const data = await response.json();
          const props = data.properties || [];
          setProperties(props);
          props.forEach(p => {
            if (p.listing_key) {
              propertyFetch(`https://tophamtechbe.com/api/treas/idx/v1/retrieve-property-media/${p.listing_key}`, {}).then(r => r.json()).then(d => {
                const first = (d.media || []).sort((a, b) => parseInt(a.order_hint) - parseInt(b.order_hint))[0];
                const url = first?.media_url || first?.s3_link;
                if (url) setPropertyImages(prev => ({ ...prev, [p.listing_key]: url }));
              }).catch(() => {});
            }
          });
        }
      } catch (error) {
        console.error('Error fetching Fountain Valley properties:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchProperties();
  }, []);

  return (
    <div className="idxtown-page">
      <Helmet>
        <title>Fountain Valley Real Estate | Homes for Sale | Truc Doan</title>
        <meta name="description" content="Explore Fountain Valley homes for sale. Top-rated schools, quiet neighborhoods, beautiful parks, and proximity to Westminster. Truc Doan, your trusted realtor." />
        <meta name="keywords" content="Fountain Valley homes for sale, Fountain Valley real estate, Fountain Valley CA houses, buy home Fountain Valley, Fountain Valley schools, Fountain Valley parks, Fountain Valley realtor, near Westminster, Truc Doan Fountain Valley" />
        <link rel="canonical" href={`${SEO_CONFIG.DOMAIN}/fountain-valley-real-estate`} />
        <meta property="og:title" content="Fountain Valley Real Estate | Homes for Sale | Truc Doan" />
        <meta property="og:description" content="Find your dream home in Fountain Valley CA. Top schools, parks, and quiet neighborhoods near Westminster and Little Saigon. Expert guidance from Truc Doan." />
        <meta property="og:url" content={`${SEO_CONFIG.DOMAIN}/fountain-valley-real-estate`} />
        <meta property="og:type" content="website" />
        <meta property="og:image" content={SEO_CONFIG.OG_IMAGE} />
        <meta name="geo.region" content="US-CA" />
        <meta name="geo.placename" content="Fountain Valley, California" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "RealEstateAgent",
            "name": "Truc Doan",
            "telephone": "+1-818-818-0989",
            "email": "doanestate@gmail.com",
            "url": `${SEO_CONFIG.DOMAIN}/fountain-valley-real-estate`,
            "image": SEO_CONFIG.OG_IMAGE,
            "description": "Truc Doan helps buyers find homes in Fountain Valley CA, known for excellent schools, beautiful parks, and quiet family neighborhoods near Westminster and Little Saigon.",
            "areaServed": {
              "@type": "City",
              "name": "Fountain Valley, California"
            },
            "address": {
              "@type": "PostalAddress",
              "addressLocality": "Westminster",
              "addressRegion": "CA",
              "postalCode": "92683",
              "addressCountry": "US"
            }
          })}
        </script>
      </Helmet>

      <div className="idxtown-container">
        <button
          onClick={() => navigate('/')}
          style={{ position: 'absolute', top: '20px', left: '20px', padding: '10px 20px', background: 'rgb(14, 165, 233)', color: 'rgb(243, 244, 246)', border: 'none', borderRadius: '5px', cursor: 'pointer', fontSize: '16px', fontWeight: 'bold', zIndex: 10 }}
        >
          {t('nav.home')}
        </button>

        <header className="idxtown-header">
          <h1>Fountain Valley Real Estate</h1>
          <p style={{ marginTop: '16px', fontSize: '18px', opacity: 0.9 }}>
            Find your dream home in Fountain Valley — top schools, beautiful parks, and quiet neighborhoods minutes from Westminster and Little Saigon.
          </p>
        </header>

        <section className="idxtown-vibe">
          <h2>Why Buy a Home in Fountain Valley?</h2>
          <p>Fountain Valley is one of Orange County's most sought-after residential communities, known for its excellent schools, meticulously maintained parks, and peaceful tree-lined streets. The city earned its nickname "A Nice Place to Live" and consistently ranks among the safest and most family-friendly cities in California. With a strong Vietnamese-American community and immediate proximity to Westminster's Little Saigon, Fountain Valley offers cultural connection alongside suburban tranquility.</p>
          <p>Homebuyers in Fountain Valley enjoy larger lot sizes, well-built single-family homes, and a strong sense of community. The Fountain Valley School District is one of the highest-performing in Orange County, making it a top choice for families with school-age children. The city's central location provides easy commuting to Irvine, Costa Mesa, Huntington Beach, and beyond.</p>
        </section>

        <section className="idxtown-lifestyle">
          <h2>Fountain Valley Neighborhoods & Amenities</h2>
          <p>Fountain Valley offers established neighborhoods with mature trees, spacious lots, and excellent community amenities throughout the city.</p>
          <div className="amenities-list">
            <div className="amenity">
              <h3>Top-Rated Schools</h3>
              <p>Fountain Valley School District and Huntington Beach Union High School District deliver exceptional academics. Fountain Valley High School is nationally recognized for STEM and AP programs.</p>
            </div>
            <div className="amenity">
              <h3>Mile Square Regional Park</h3>
              <p>A 640-acre urban park featuring golf courses, lakes, sports fields, walking trails, and picnic areas. One of Orange County's premier recreational spaces.</p>
            </div>
            <div className="amenity">
              <h3>Quiet Family Neighborhoods</h3>
              <p>Well-maintained single-family home communities with wide streets, mature landscaping, and active HOAs that keep property values strong.</p>
            </div>
            <div className="amenity">
              <h3>Near Westminster & Little Saigon</h3>
              <p>Just minutes from Little Saigon's restaurants, markets, and cultural events. Enjoy the best Vietnamese cuisine and community without the density.</p>
            </div>
            <div className="amenity">
              <h3>Recreation Center</h3>
              <p>The Fountain Valley Recreation Center and Community Center host youth sports, senior programs, classes, and community events year-round.</p>
            </div>
            <div className="amenity">
              <h3>Shopping & Dining</h3>
              <p>Fountain Valley Promenade, Costco, and numerous dining options along Brookhurst and Warner. Close to South Coast Plaza and Bella Terra.</p>
            </div>
          </div>
        </section>

        <section className="market-snapshot">
          <h2>Fountain Valley Real Estate Market Snapshot</h2>
          <p>Fountain Valley commands premium pricing due to its excellent schools, parks, and family-friendly reputation. Homes sell quickly in this competitive market.</p>
          <table className="market-stats">
            <thead>
              <tr>
                <th>Market Stat</th>
                <th>Value</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Median Home Price (Fountain Valley)</td>
                <td>~$1,050,000</td>
              </tr>
              <tr>
                <td>Average Days on Market</td>
                <td>20–30 Days</td>
              </tr>
              <tr>
                <td>Most Popular Price Range</td>
                <td>$800K – $1.3M</td>
              </tr>
              <tr>
                <td>Property Types</td>
                <td>Single Family, Townhomes</td>
              </tr>
              <tr>
                <td>Top Draws</td>
                <td>Schools, Parks, Safety, Location</td>
              </tr>
            </tbody>
          </table>
        </section>

        <section className="properties-section">
          <h2>Current Homes for Sale in Fountain Valley</h2>
          {loading ? (
            <p style={{ color: 'rgb(156, 163, 175)' }}>{t('property.loadingProperties')}</p>
          ) : properties.length > 0 ? (
            <div className="properties-grid">
              {properties.slice(0, 9).map((property) => (
                <div
                  key={property.listing_key}
                  className="property-card"
                  onClick={() => navigate(`/listing/${property.listing_key}`)}
                  style={{
                    backgroundImage: (propertyImages[property.listing_key] || property.first_photo_url) ? `url(${propertyImages[property.listing_key] || property.first_photo_url})` : 'none',
                    backgroundSize: 'cover',
                    backgroundPosition: 'center'
                  }}
                >
                  <div className="property-price">${property.current_price?.toLocaleString()}</div>
                  <div className="property-address">
                    {formatPropertyAddress(property)}, CA {property.postal_code}
                  </div>
                  <div className="property-type">{property.property_sub_type}</div>
                  {(property.list_agent_full_name || property.list_office_name) && (
                    <div style={{fontSize: '11px', color: '#ddd', marginTop: '2px'}}>{t('landing.listedBy')}: {[property.list_agent_full_name, property.list_office_name].filter(Boolean).join(' | ')}</div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <p style={{ color: 'rgb(156, 163, 175)' }}>
              Browse all available Fountain Valley listings by clicking "Search Listings" below.
            </p>
          )}
        </section>

        <section className="cta-section">
          <h2>Ready to Buy or Sell in Fountain Valley?</h2>
          <p>Contact Truc Doan today for a free consultation. I'll help you navigate Fountain Valley's competitive market and find the perfect home for your family.</p>
          <div className="cta-buttons">
            <a href="mailto:doanestate@gmail.com?subject=Fountain Valley Real Estate Inquiry" className="cta-button primary">
              Email Truc Doan
            </a>
            <a href="tel:+1-818-818-0989" className="cta-button secondary">
              Call +1-818-818-0989
            </a>
            <button className="cta-button primary" onClick={() => navigate('/chat')} style={{ border: 'none', cursor: 'pointer' }}>
              Search Fountain Valley Listings
            </button>
          </div>
        </section>
      </div>

      <div style={{ background: 'rgba(0,0,0,0.85)', padding: '6px 12px', display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
        <img src="https://aw-client-logos-and-headshots.s3.us-east-2.amazonaws.com/logos/crmlslogo.png" alt="CRMLS" style={{ height: '20px', objectFit: 'contain' }} />
        <img src="https://aw-client-logos-and-headshots.s3.us-east-2.amazonaws.com/logos/equalhousinglogo.png" alt="Equal Housing Opportunity" style={{ height: '20px', objectFit: 'contain' }} />
        <span style={{ fontSize: '10px', color: '#ccc', flex: 1 }}>
          Based on information from California Regional Multiple Listing Service, Inc. and/or other sources. All data, including all measurements and calculations of area, is obtained from various sources and has not been, and will not be, verified by broker or MLS. All information should be independently reviewed and verified for accuracy. Properties may or may not be listed by the office/agent presenting the information.
        </span>
      </div>
      <SEOLinks />
    </div>
  );
};

export default FountainValleyRealEstatePage;
